import * as Y from 'yjs';
import * as encoding from 'lib0/encoding';
import * as decoding from 'lib0/decoding';
import * as syncProtocol from 'y-protocols/sync';
import * as awarenessProtocol from 'y-protocols/awareness';
import {
  MSG_AWARENESS,
  MSG_SUBSCRIBE,
  MSG_SYNC,
  decodeFrame,
  encodeFrame,
} from '@rbv/shared';
import { IDB_ORIGIN } from './persistence.js';

export type SyncStatus = 'connecting' | 'connected' | 'offline';

interface DocEntry {
  doc: Y.Doc;
  awareness?: awarenessProtocol.Awareness;
}

/**
 * One WebSocket, many docs: multiplexed y-protocols sync + awareness client.
 * Reconnects with backoff; the per-doc step1/step2 handshake on every
 * (re)connect converges offline edits in both directions.
 */
export class SyncProvider {
  private ws: WebSocket | null = null;
  private docs = new Map<string, DocEntry>();
  private backoff = 500;
  private closed = false;
  status: SyncStatus = 'connecting';
  private statusListeners = new Set<(s: SyncStatus) => void>();

  constructor(private url: string) {}

  onStatus(cb: (s: SyncStatus) => void): () => void {
    this.statusListeners.add(cb);
    cb(this.status);
    return () => this.statusListeners.delete(cb);
  }

  private setStatus(s: SyncStatus) {
    if (this.status === s) return;
    this.status = s;
    for (const cb of this.statusListeners) cb(s);
  }

  connect() {
    if (this.closed) return;
    this.setStatus('connecting');
    const ws = new WebSocket(this.url);
    ws.binaryType = 'arraybuffer';
    this.ws = ws;
    ws.onopen = () => {
      this.backoff = 500;
      this.setStatus('connected');
      for (const key of this.docs.keys()) this.handshake(key);
    };
    ws.onmessage = (ev) => {
      try {
        this.onFrame(new Uint8Array(ev.data as ArrayBuffer));
      } catch (err) {
        console.error('[sync] bad frame', err);
      }
    };
    ws.onclose = () => {
      this.ws = null;
      this.setStatus('offline');
      if (!this.closed) {
        setTimeout(() => this.connect(), this.backoff);
        this.backoff = Math.min(this.backoff * 2, 15_000);
      }
    };
    ws.onerror = () => ws.close();
  }

  destroy() {
    this.closed = true;
    this.ws?.close();
  }

  private send(frame: Uint8Array) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(frame);
  }

  private handshake(docKey: string) {
    const entry = this.docs.get(docKey);
    if (!entry) return;
    this.send(encodeFrame(MSG_SUBSCRIBE, docKey));
    const enc = encoding.createEncoder();
    syncProtocol.writeSyncStep1(enc, entry.doc);
    this.send(encodeFrame(MSG_SYNC, docKey, encoding.toUint8Array(enc)));
    if (entry.awareness && entry.awareness.getLocalState() !== null) {
      const aw = awarenessProtocol.encodeAwarenessUpdate(entry.awareness, [
        entry.awareness.clientID,
      ]);
      this.send(encodeFrame(MSG_AWARENESS, docKey, aw));
    }
  }

  /** Register a doc for syncing. Safe to call before or after connect(). */
  addDoc(docKey: string, doc: Y.Doc) {
    if (this.docs.has(docKey)) return;
    const entry: DocEntry = { doc };
    this.docs.set(docKey, entry);
    doc.on('update', (update: Uint8Array, origin: unknown) => {
      if (origin === this || origin === IDB_ORIGIN) return;
      const enc = encoding.createEncoder();
      syncProtocol.writeUpdate(enc, update);
      this.send(encodeFrame(MSG_SYNC, docKey, encoding.toUint8Array(enc)));
    });
    if (this.ws?.readyState === WebSocket.OPEN) this.handshake(docKey);
  }

  /** Attach an Awareness instance for a doc (open-note presence/cursors). */
  addAwareness(docKey: string, awareness: awarenessProtocol.Awareness) {
    const entry = this.docs.get(docKey);
    if (!entry || entry.awareness) return;
    entry.awareness = awareness;
    awareness.on('update', ({ added, updated, removed }: { added: number[]; updated: number[]; removed: number[] }, origin: unknown) => {
      if (origin === this) return;
      const changed = [...added, ...updated, ...removed];
      const aw = awarenessProtocol.encodeAwarenessUpdate(awareness, changed);
      this.send(encodeFrame(MSG_AWARENESS, docKey, aw));
    });
  }

  private onFrame(data: Uint8Array) {
    const { type, docKey, payload } = decodeFrame(data);
    const entry = this.docs.get(docKey);
    if (!entry) return;
    if (type === MSG_SYNC) {
      const dec = decoding.createDecoder(payload);
      const enc = encoding.createEncoder();
      syncProtocol.readSyncMessage(dec, enc, entry.doc, this);
      if (encoding.length(enc) > 0) {
        this.send(encodeFrame(MSG_SYNC, docKey, encoding.toUint8Array(enc)));
      }
    } else if (type === MSG_AWARENESS && entry.awareness) {
      awarenessProtocol.applyAwarenessUpdate(entry.awareness, payload, this);
    }
  }
}
